import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { TOOLS } from './game';

const TICK_DELAYS = [65, 75, 90, 110, 140, 175, 220, 285];
const formatRoll = (roll) => String(roll).padStart(2, '0');

const ClashReveal = ({
  game,
  room,
  preview,
  onMirror,
  onChisel,
  onSwap,
  onMemory,
  onResolve,
}) => {
  const [display, setDisplay] = useState({ player: null, enemy: null });
  const [revealed, setRevealed] = useState(false);
  const timers = useRef([]);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;

  const reveal = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setDisplay({ player: game.playerRoll, enemy: game.enemyRoll });
    setRevealed(true);
  };

  useEffect(() => {
    if (reduceMotion) {
      reveal();
      return undefined;
    }
    let elapsed = 0;
    TICK_DELAYS.forEach((delay) => {
      elapsed += delay;
      timers.current.push(
        setTimeout(() => {
          setDisplay({
            player: Math.floor(Math.random() * 100),
            enemy: Math.floor(Math.random() * 100),
          });
        }, elapsed)
      );
    });
    timers.current.push(setTimeout(reveal, elapsed + 240));
    return () => timers.current.forEach(clearTimeout);
    // The suspense sequence runs once. Artifact changes update the revealed values below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (revealed)
      setDisplay({ player: game.playerRoll, enemy: game.enemyRoll });
  }, [game.playerRoll, game.enemyRoll, revealed]);

  const outcomeText =
    preview.outcome === 'strike'
      ? `You will deal ${preview.damageToEnemy} damage.`
      : preview.outcome === 'hit'
      ? `${room.name} will deal ${preview.damageToPlayer} damage.`
      : 'A tie damages both sides.';
  const canUseTool = revealed && !game.manipulationUsed;

  return (
    <section className="dungeonPanel dungeonClash">
      <span className="dungeonEyebrow">CLASH / {room.name.toUpperCase()}</span>
      <h2>
        {revealed ? 'Fate has chosen its numbers' : 'Rolling in the dark…'}
      </h2>
      <span className="dungeonSrOnly" role="status">
        {revealed
          ? `You rolled ${game.playerRoll}. ${room.name} rolled ${game.enemyRoll}. ${outcomeText}`
          : 'Both rolls are in progress.'}
      </span>

      <div className="dungeonRollArena" aria-hidden="true">
        <div className="dungeonCombatant">
          <span>You</span>
          <motion.div
            className="dungeonRoll dungeonPlayerRoll"
            animate={
              reduceMotion
                ? {}
                : revealed
                ? { scale: [1.15, 0.95, 1] }
                : { rotate: [0, -3, 3, 0] }
            }
            transition={
              revealed
                ? { duration: 0.35 }
                : { duration: 0.3, repeat: Infinity }
            }
          >
            {display.player === null ? '??' : formatRoll(display.player)}
          </motion.div>
        </div>
        <span className="dungeonVersus">VS</span>
        <div className="dungeonCombatant">
          <span>{room.name}</span>
          <motion.div
            className="dungeonRoll dungeonEnemyRoll"
            animate={
              reduceMotion
                ? {}
                : revealed
                ? { scale: [1.15, 0.95, 1] }
                : { rotate: [0, 3, -3, 0] }
            }
            transition={
              revealed
                ? { duration: 0.35, delay: 0.1 }
                : { duration: 0.3, repeat: Infinity }
            }
          >
            {display.enemy === null ? '??' : formatRoll(display.enemy)}
          </motion.div>
        </div>
      </div>

      {revealed ? (
        <>
          <p className={`dungeonForecast dungeonForecast-${preview.outcome}`}>
            {outcomeText}
          </p>
          <p className="dungeonRule">
            A winning pair deals 2 damage. An enemy pair deals 1 extra damage. A
            tie hurts both.
          </p>
          <div className="dungeonToolActions">
            <button
              disabled={!canUseTool || game.tools.mirror < 1}
              onClick={onMirror}
            >
              <strong>
                {TOOLS.mirror.name} · {game.tools.mirror}
              </strong>
              <span>
                Reverse to{' '}
                {formatRoll(
                  Number(
                    formatRoll(game.playerRoll).split('').reverse().join('')
                  )
                )}
              </span>
            </button>
            <button
              disabled={!canUseTool || game.tools.chisel < 1}
              onClick={() => onChisel(0)}
            >
              <strong>
                {TOOLS.chisel.name} · {game.tools.chisel}
              </strong>
              <span>Reroll first digit</span>
            </button>
            <button
              disabled={!canUseTool || game.tools.chisel < 1}
              onClick={() => onChisel(1)}
            >
              <strong>
                {TOOLS.chisel.name} · {game.tools.chisel}
              </strong>
              <span>Reroll second digit</span>
            </button>
            <button
              disabled={!canUseTool || game.tools.swap < 1}
              onClick={onSwap}
            >
              <strong>
                {TOOLS.swap.name} · {game.tools.swap}
              </strong>
              <span>Swap both rolls</span>
            </button>
            <button
              disabled={
                !canUseTool ||
                (game.storedRoll === null && game.tools.memory < 1)
              }
              onClick={onMemory}
            >
              <strong>
                {TOOLS.memory.name} · {game.tools.memory}
              </strong>
              <span>
                {game.storedRoll === null
                  ? 'Store yours and reroll'
                  : `Recall ${formatRoll(game.storedRoll)}`}
              </span>
            </button>
          </div>
          {game.manipulationUsed && (
            <p className="dungeonArtifactUsed">
              Artifact used. Fate is locked for this clash.
            </p>
          )}
          <button className="dungeonPrimary" onClick={onResolve}>
            Resolve clash →
          </button>
        </>
      ) : (
        <button className="dungeonSkip" onClick={reveal}>
          Skip animation
        </button>
      )}
    </section>
  );
};

export default ClashReveal;
